import _extends from "@babel/runtime/helpers/extends";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import UpgradeTheme from '@upgrade/themes/themes/upgrade';
import { createPortal } from 'react-dom';
import { StickyContainer, ScreenReaderLink } from './styles';
import Spacer from './Spacer';
import useStacked from '../SubNavigation/useStacked';
import { SUB_NAVIGATION_ID } from '../SubNavigation/constants';
import useWindowMinWidth from '../hooks/useWindowMinWidth';
import { isActivationKey } from '../utils/event-key-names';
import warning from '../utils/console-deprecated';

/**
 * StickyActions is a component that renders a sticky actions area on the page,
 * It allows the user to navigate to the actions area using keyboard navigation.
 *
 * The children prop should be a function that returns the content of the actions area.
 *
 * The injected props in the children must be spread on interactive elements to enable/disable
 * keyboard navigation and maintain tab order.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {ReactNode} props.children - The content of the actions area.
 * @param {HTMLElement} props.domNode - The DOM node to render the sticky actions area into.
 * @param {string} props.breakpoint - The breakpoint at which the sticky actions area should be hidden.
 * @param {number} props.gap - The gap between the sticky actions area and the content.
 * @param {string|React.RefObject} props.navIdOrRef - The ID or ref of the navigation element to stack with.
 * @param {boolean} props.hideChildren - Whether to hide the children when the sticky actions area is not rendered.
 * @param {Object} props.containerProps - Additional props to spread to the StickyContainer component.
 * @returns {ReactNode} The rendered StickyActions component.
 */
const StickyActions = ({
  children,
  domNode,
  breakpoint = 'sm',
  gap = 'xl',
  navIdOrRef = SUB_NAVIGATION_ID,
  hideChildren = true,
  ...containerProps
}) => {
  const [matchesBreakpoint] = useWindowMinWidth(breakpoint);
  const ref = useRef();
  const returnToPageRef = useRef();
  const [refState, setRefState] = useState();
  const [node, setNode] = useState();
  const [focusInsidePortal, setFocusInsidePortal] = useState(false);
  useStacked({
    currentElement: refState,
    navIdOrRef
  });
  const shouldStick = !matchesBreakpoint || breakpoint === 'none';
  const handleRef = useCallback(newRef => {
    ref.current = newRef;
    setRefState(newRef);
  }, []);
  useEffect(() => {
    if (typeof document !== 'undefined') {
      setNode(domNode || document.body);
    }
  }, [domNode]);
  const handleJumpToActions = event => {
    if (isActivationKey(event.key)) {
      ref.current?.focus();
      event.preventDefault();
      setFocusInsidePortal(true);
    }
  };
  const handleReturnToPage = event => {
    if (isActivationKey(event.key)) {
      returnToPageRef.current?.focus();
      event.preventDefault();
      setFocusInsidePortal(false);
    }
  };

  // istanbul ignore next
  if (typeof children !== 'function') {
    warning('DEPRECATED: StickyActions / children should be a function');
  }
  if (node && shouldStick) {
    const keyboardFocusProps = {
      tabIndex: focusInsidePortal ? 0 : -1
    };
    const content = typeof children === 'function' ? children(keyboardFocusProps) : children;
    const portal = /*#__PURE__*/createPortal(/*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(StickyContainer, _extends({
      id: 'sticky-actions',
      'aria-label': 'Actions area',
      ref: handleRef
    }, keyboardFocusProps, containerProps), content, /*#__PURE__*/React.createElement(ScreenReaderLink, _extends({
      onKeyDown: handleReturnToPage,
      href: "#return-to-page"
    }, keyboardFocusProps), "Return to page")), /*#__PURE__*/React.createElement(Spacer, {
      domNode: node,
      breakpoint,
      gap,
      sourceRef: ref
    })), node);
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ScreenReaderLink, {
      id: "return-to-page",
      ref: returnToPageRef,
      onKeyDown: handleJumpToActions,
      href: "#sticky-actions"
    }, "Navigate to actions"), portal);
  }
  if (hideChildren) {
    return null;
  }
  return typeof children === 'function' ? children({}) : children;
};
StickyActions.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  domNode: PropTypes.object,
  navIdOrRef: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    current: PropTypes.object
  })]),
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  // breakpoint="none" to always stick children; "sm" will stick children when window is smaller than the "sm" breakpoint
  breakpoint: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'none']),
  gap: PropTypes.oneOf(Object.keys(UpgradeTheme.spacing)),
  // if true, hides children when they are sticky, if false it shows children in place when they are not sticky
  hideChildren: PropTypes.bool
};

// NOTE: Due to the usage of a react Portal, you must use an id
// to link your submit button to your Form.
//
// <Form id="my-form">
//   {...}
//   <StickyActions breakpoint="sm">
//     <Button submit buttonProps={{ form: 'my-form' }}>
//       Continue
//     </Button>
//   </StickyActions>
// </Form>
export default StickyActions;
//# sourceMappingURL=index.js.map